import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Wrench, Cog, Send, Save, DollarSign, CheckCircle2 } from "lucide-react";

export default function EstimateEditor() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");
  const [estimateId, setEstimateId] = useState(null);
  const [job, setJob] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: est }, { data: jobs }] = await Promise.all([
        api.get(`/jobs/${jobId}/estimate`),
        api.get(`/jobs`),
      ]);
      const j = jobs.find((x) => x.id === jobId);
      setJob(j);
      if (est) {
        setEstimateId(est.id);
        setItems(est.items || []);
        setTaxRate(est.tax_rate || 0);
        setNotes(est.notes || "");
        setStatus(est.status || "draft");
      }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [jobId]);

  const totals = useMemo(() => {
    let subtotal = 0;
    for (const it of items) {
      const qty = Number(it.quantity) || 1;
      const price = Number(it.unit_price) || 0;
      let line = qty * price;
      if (it.kind === "labor" && it.hours) line = Number(it.hours) * price;
      subtotal += line;
    }
    const tax = subtotal * (Number(taxRate) || 0);
    return { subtotal: +subtotal.toFixed(2), tax: +tax.toFixed(2), total: +(subtotal + tax).toFixed(2) };
  }, [items, taxRate]);

  const addPart = () => setItems((p) => [...p, { kind: "part", description: "", part_number: "", quantity: 1, unit_price: 0 }]);
  const addLabor = () => setItems((p) => [...p, { kind: "labor", description: "", hours: 1, unit_price: 95 }]);
  const updateItem = (i, patch) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, ...patch } : it));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        items: items.map((it) => ({
          kind: it.kind,
          description: it.description,
          part_number: it.part_number || null,
          quantity: Number(it.quantity) || 1,
          unit_price: Number(it.unit_price) || 0,
          hours: it.kind === "labor" ? (Number(it.hours) || null) : null,
        })),
        tax_rate: Number(taxRate) || 0,
        notes,
      };
      const { data } = await api.post(`/jobs/${jobId}/estimate`, payload);
      setEstimateId(data.id);
      setStatus(data.status);
      toast.success("Orçamento salvo");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const sendEstimate = async () => {
    if (!estimateId) { await save(); }
    const id = estimateId || (await api.get(`/jobs/${jobId}/estimate`)).data?.id;
    if (!id) return toast.error("Salve o orçamento antes");
    if (items.length === 0) return toast.error("Adicione ao menos um item");
    try {
      const { data } = await api.post(`/estimates/${id}/send`);
      setStatus(data.status);
      toast.success("Orçamento enviado ao cliente. Job movido para 'Aguardando Aprovação'.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar"); }
  };

  if (loading) return <div className="text-sm text-muted-foreground">Carregando…</div>;

  return (
    <div className="max-w-5xl mx-auto space-y-6" data-testid="estimate-editor-page">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2"><ArrowLeft className="w-4 h-4" /> Voltar</Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={save} disabled={saving} data-testid="estimate-save-btn"><Save className="w-4 h-4" /> {saving ? "Salvando…" : "Salvar Rascunho"}</Button>
          <Button onClick={sendEstimate} disabled={saving || items.length === 0} data-testid="estimate-send-btn"><Send className="w-4 h-4" /> Enviar ao Cliente</Button>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Orçamento</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {job?.vehicle?.year} {job?.vehicle?.make} {job?.vehicle?.model}
        </h1>
        <div className="text-sm text-muted-foreground">{job?.client_name} · {job?.service_type}</div>
        <div className="mt-2 flex items-center gap-2">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${status === "approved" ? "bg-emerald-500/15 text-emerald-500" : status === "sent" ? "bg-orange-500/15 text-orange-500" : "bg-secondary text-muted-foreground"}`}>
            {status === "approved" ? "Aprovado" : status === "sent" ? "Enviado" : "Rascunho"}
          </span>
          {status === "approved" && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
        </div>
      </div>

      {/* Items */}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="px-4 py-2.5 bg-secondary/60 border-b border-border flex items-center justify-between">
          <div className="text-sm font-semibold uppercase tracking-wider">Itens</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={addPart} data-testid="add-part-btn"><Cog className="w-3.5 h-3.5" /> Adicionar Peça</Button>
            <Button size="sm" variant="outline" onClick={addLabor} data-testid="add-labor-btn"><Wrench className="w-3.5 h-3.5" /> Adicionar Mão de Obra</Button>
          </div>
        </div>
        {items.length === 0 ? <div className="p-8 text-center text-sm text-muted-foreground">Nenhum item. Adicione peças e mão de obra acima.</div> :
          <div className="divide-y divide-border">
            {items.map((it, i) => (
              <div key={i} className="p-3 grid grid-cols-12 gap-2 items-center" data-testid={`estimate-item-${i}`}>
                <div className="col-span-1 flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {it.kind === "part" ? <><Cog className="w-3 h-3" />Peça</> : <><Wrench className="w-3 h-3" />M.O.</>}
                </div>
                <Input className="col-span-4 h-8 text-sm" placeholder="Descrição" value={it.description} onChange={(e) => updateItem(i, { description: e.target.value })} data-testid={`item-desc-${i}`} />
                {it.kind === "part" ?
                  <Input className="col-span-2 h-8 text-xs font-mono" placeholder="Part #" value={it.part_number || ""} onChange={(e) => updateItem(i, { part_number: e.target.value })} /> :
                  <Input className="col-span-2 h-8 text-sm" type="number" step="0.5" placeholder="Horas" value={it.hours || ""} onChange={(e) => updateItem(i, { hours: e.target.value })} />}
                <Input className="col-span-1 h-8 text-sm" type="number" step="0.5" placeholder={it.kind === "labor" ? "" : "Qtd"} value={it.kind === "labor" ? 1 : (it.quantity || 1)} onChange={(e) => updateItem(i, { quantity: e.target.value })} disabled={it.kind === "labor"} />
                <div className="col-span-2 relative">
                  <DollarSign className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input className="h-8 text-sm pl-6" type="number" step="0.01" placeholder={it.kind === "labor" ? "Hora ($)" : "Preço ($)"} value={it.unit_price || 0} onChange={(e) => updateItem(i, { unit_price: e.target.value })} data-testid={`item-price-${i}`} />
                </div>
                <div className="col-span-1 text-right text-sm font-mono">
                  ${((it.kind === "labor" ? (Number(it.hours) || 0) : (Number(it.quantity) || 1)) * (Number(it.unit_price) || 0)).toFixed(2)}
                </div>
                <button onClick={() => removeItem(i)} className="col-span-1 justify-self-end p-1 rounded hover:bg-accent text-muted-foreground" data-testid={`item-remove-${i}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>}
      </div>

      {/* Totals + notes */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 border border-border rounded-md bg-card p-4">
          <Label>Notas / termos</Label>
          <Textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ex: Validade 15 dias. Peças com garantia de 12 meses." />
        </div>
        <div className="border border-border rounded-md bg-card p-4 space-y-2">
          <div className="flex items-center gap-2">
            <Label className="flex-1">Imposto (%)</Label>
            <Input type="number" step="0.01" className="w-24 h-8 text-sm text-right" value={taxRate * 100} onChange={(e) => setTaxRate((Number(e.target.value) || 0) / 100)} data-testid="tax-rate-input" />
          </div>
          <div className="border-t border-border pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">${totals.subtotal.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Imposto</span><span className="font-mono">${totals.tax.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-border pt-1.5 mt-1.5">
              <span className="font-display font-bold">TOTAL</span>
              <span className="font-display font-bold text-lg" data-testid="estimate-total">${totals.total.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
